import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { ADMIN_API_BASE } from "../utils/apiConfig";

export default function useCourseSections(courseId) {
    const [sections, setSections] = useState([]);
    const [loading, setLoading] = useState(true);

    // =========================
    // FETCH SECTIONS
    // =========================
    const fetchSections = useCallback(async () => {
        if (!courseId) return;

        setLoading(true);

        try {
            const res = await axios.get(
                `${ADMIN_API_BASE}/course/${courseId}/section/`
            );

            const raw = res.data.sections || [];

            const normalized = raw.map((section) => ({
                ...section,

                // preserve original id for editing
                _originalId: section.id,

                contents: (section.contents || []).map((content, idx) => ({
                    ...content,

                    // ensure level always exists
                    level: content.level ?? idx + 1,

                    // mark existing content
                    _isNew: false,
                    _deleted: false
                }))
            }));

            setSections(normalized);
        } catch (err) {
            console.error("fetchSections error:", err);
        } finally {
            setLoading(false);
        }
    }, [courseId]);

    // auto-fetch on mount/course change
    useEffect(() => {
        fetchSections();
    }, [fetchSections]);

    // =========================
    // CREATE SECTION
    // =========================
    const createSection = async (payload) => {
        try {
            const res = await axios.post(
                `${ADMIN_API_BASE}/course/${courseId}/section/`,
                payload
            );

            const created = res.data.section;

            setSections((prev) => [
                ...prev,
                {
                    ...created,
                    _originalId: created.id,
                    contents: created.contents || []
                }
            ]);

            return created;
        } catch (err) {
            console.error(
                "createSection error:",
                err.response?.data || err
            );
            throw err;
        }
    };

    // =========================
    // UPDATE SECTION
    // =========================
    const updateSection = async (sectionId, payload) => {
        try {
            const res = await axios.put(
                `${ADMIN_API_BASE}/course/${courseId}/section/${sectionId}/`,
                payload
            );

            const updated = res.data.section;

            setSections((prev) =>
                prev.map((s) =>
                    String(s.id) === String(sectionId)
                        ? {
                            ...updated,
                            _originalId: updated.id,
                            contents: updated.contents || []
                        }
                        : s
                )
            );

            return updated;
        } catch (err) {
            console.error(
                "updateSection error:",
                err.response?.data || err
            );
            throw err;
        }
    };

    // =========================
    // DELETE SECTION
    // =========================
    const deleteSection = async (sectionId) => {
        try {
            await axios.delete(
                `${ADMIN_API_BASE}/course/${courseId}/section/${sectionId}/`
            );

            setSections((prev) =>
                prev.filter((s) => String(s.id) !== String(sectionId))
            );
        } catch (err) {
            console.error(
                "deleteSection error:",
                err.response?.data || err
            );
            throw err;
        }
    };

    // =========================
    // CREATE CONTENT
    // =========================
    const createContent = async (sectionId, contentPayload) => {
        try {
            console.log("creating content:", contentPayload);

            const res = await axios.post(
                `${ADMIN_API_BASE}/course/${courseId}/section/${sectionId}/content/`,
                contentPayload
            );

            const created = res.data.content;

            setSections((prev) =>
                prev.map((section) =>
                    String(section.id) === String(sectionId)
                        ? {
                            ...section,
                            contents: [
                                ...(section.contents || []),
                                created
                            ]
                        }
                        : section
                )
            );

            return created;
        } catch (err) {
            console.error(
                "createContent error:",
                err.response?.data || err
            );
            throw err;
        }
    };

    // =========================
    // DELETE CONTENT
    // =========================
    const deleteContent = async (sectionId, contentId) => {
        try {
            await axios.delete(
                `${ADMIN_API_BASE}/course/${courseId}/section/${sectionId}/content/${contentId}`
            );

            setSections((prev) =>
                prev.map((section) =>
                    String(section.id) === String(sectionId)
                        ? {
                            ...section,
                            contents: (section.contents || []).filter(
                                (c) =>
                                    String(c.id) !== String(contentId)
                            )
                        }
                        : section
                )
            );
        } catch (err) {
            console.error(
                "deleteContent error:",
                err.response?.data || err
            );
            throw err;
        }
    };

    // =========================
    // REORDER SECTIONS
    // =========================
    const reorderSections = async (reorderedSections) => {
        try {
            const positioned = reorderedSections.map((s, idx) => ({
                ...s,
                position: idx + 1
            }));

            setSections(positioned);

            const payload = positioned.map((s) => ({
                id: s.id,
                position: s.position
            }));

            await axios.put(
                `${ADMIN_API_BASE}/course/${courseId}/section/reorder/`,
                {
                    sections: payload
                }
            );
        } catch (err) {
            console.error(
                "reorderSections error:",
                err.response?.data || err
            );
            throw err;
        }
    };

    // =========================
    // REORDER CONTENTS
    // =========================
    const reorderContents = async (
        sectionId,
        reorderedContents
    ) => {
        try {
            const updated = reorderedContents.map((c, idx) => ({
                ...c,
                level: idx + 1
            }));

            setSections((prev) =>
                prev.map((section) =>
                    String(section.id) === String(sectionId)
                        ? {
                            ...section,
                            contents: updated
                        }
                        : section
                )
            );

            const payload = updated.map((c) => ({
                id: c.id,
                level: c.level
            }));

            await axios.put(
                `${ADMIN_API_BASE}/course/${courseId}/section/${sectionId}/content/reorder/`,
                {
                    contents: payload
                }
            );
        } catch (err) {
            console.error(
                "reorderContents error:",
                err.response?.data || err
            );
            throw err;
        }
    };

    return {
        sections,
        setSections,
        loading,

        fetchSections,

        createSection,
        updateSection,
        deleteSection,

        createContent,
        deleteContent,

        reorderSections,
        reorderContents
    };
}
